import {
  Badge,
  Box,
  Flex,
  HStack,
  Code,
  Text,
  Drawer,
  DrawerBody,
  DrawerHeader,
  DrawerOverlay,
  DrawerContent,
  DrawerCloseButton,
  Accordion,
  AccordionItem,
  AccordionButton,
  AccordionPanel,
  BadgeProps,
  Alert,
  AlertIcon,
  VStack,
} from '@chakra-ui/react';
import { trpcAdmin } from '@duke-hq/app-admin-web/lib/trpcAdmin';
import { JsonViewer } from '@duke-hq/ui/components/JsonViewer/JsonViewer';
import { MinusIcon, PlusIcon } from 'lucide-react';
import { Link } from '../components/Link';
export function MigrationRuns({
  isOpen,
  onClose,
  procedureName,
}: {
  isOpen: boolean;
  onClose: () => void;

  procedureName?: string;
}): React.ReactElement | null {
  if (!isOpen) {
    return null;
  }

  if (typeof procedureName === 'string') {
    return <MigrationList procedureName={procedureName} isOpen={isOpen} onClose={onClose} />;
  }

  return (
    <Drawer isOpen={isOpen} onClose={onClose} size="xl">
      <DrawerOverlay />
      <DrawerContent>
        <DrawerHeader>
          <DrawerCloseButton onClick={onClose} />
        </DrawerHeader>
        <DrawerBody>
          <Alert status="warning">
            <AlertIcon />
            <Text>Please select a migration procedure to view its runs.</Text>
          </Alert>
        </DrawerBody>
      </DrawerContent>
    </Drawer>
  );
}

export function MigrationList({
  isOpen,
  onClose,
  procedureName,
}: {
  isOpen: boolean;
  onClose: () => void;

  procedureName: string;
}): React.ReactElement {
  const { data } = trpcAdmin.migrations.listRuns.useQuery({
    procedureName: procedureName,
  });
  return (
    <Drawer isOpen={isOpen} onClose={onClose} size="xl">
      <DrawerOverlay />
      <DrawerContent>
        <DrawerHeader style={{ padding: '0px' }}>
          <DrawerCloseButton onClick={onClose} />
        </DrawerHeader>
        <Box p="4">
          <Text fontSize="xl" fontWeight="bold">
            Migration Runs <Code>{procedureName}</Code>
          </Text>
        </Box>

        <DrawerBody p="0" border="none" m="0" paddingInline="0 !important">
          {typeof procedureName === 'string' &&
          typeof data !== 'undefined' &&
          'items' in data &&
          Array.isArray(data.items) &&
          data.items.length > 0 ? (
            <Accordion allowMultiple defaultIndex={[0]}>
              {data.items.map((run) => (
                <AccordionItem key={run.id}>
                  {({ isExpanded }) => {
                    let statusColor: BadgeProps['colorScheme'];

                    switch (run?.status) {
                      case 'SUCCESS':
                        statusColor = 'green';
                        break;
                      case 'RUNNING':
                        statusColor = 'purple';
                        break;
                      case 'FAILURE':
                        statusColor = 'red';
                        break;
                      default:
                        statusColor = 'orange';
                    }

                    return (
                      <>
                        <h2>
                          <AccordionButton>
                            <Box as="span" flex="10" textAlign="left">
                              {run.id}
                            </Box>
                            <Badge flex="1" colorScheme={statusColor}>
                              {run.status}
                            </Badge>

                            {isExpanded ? <MinusIcon fontSize="xs" /> : <PlusIcon fontSize="xs" />}
                          </AccordionButton>
                        </h2>
                        <AccordionPanel pb={4}>
                          <Box p={3} borderWidth="1px" borderRadius="md" mb={4}>
                            <Flex direction="column" gap={2}>
                              <HStack>
                                {typeof run.traceUrl === 'string' ? (
                                  <>
                                    <Text fontWeight="bold" width="120px">
                                      Trace:
                                    </Text>
                                    <Link to={run.traceUrl} target="_blank">
                                      {run.traceId}
                                    </Link>
                                  </>
                                ) : null}
                              </HStack>
                              <HStack>
                                <Text fontWeight="bold" width="120px">
                                  Created by:
                                </Text>
                                <Text>{run.created_by}</Text>
                              </HStack>
                              <HStack>
                                <Text fontWeight="bold" width="120px">
                                  Created at:
                                </Text>
                                <Text>{run.created_at.toLocaleString()}</Text>
                              </HStack>
                              <HStack>
                                <Text fontWeight="bold" width="120px">
                                  Updated at:
                                </Text>
                                <Text>{run.updated_at.toLocaleString()}</Text>
                              </HStack>
                              <HStack>
                                <Text fontWeight="bold" width="120px">
                                  Finished at:
                                </Text>
                                <Text>
                                  {typeof run.finished_at !== 'undefined' &&
                                  run.finished_at !== null ? (
                                    run.finished_at.toLocaleString()
                                  ) : (
                                    <Badge colorScheme="yellow">Not finished</Badge>
                                  )}
                                </Text>
                              </HStack>
                            </Flex>
                          </Box>
                          <VStack w="full">
                            {typeof run.input === 'string' ? (
                              <JsonViewer data={run.input} title="Input" />
                            ) : null}
                            {typeof run.result === 'string' ? (
                              <JsonViewer data={run.result} title="Result" />
                            ) : null}
                            {typeof run.meta === 'string' ? (
                              <JsonViewer data={run.meta} title="Meta" />
                            ) : null}
                          </VStack>
                        </AccordionPanel>
                      </>
                    );
                  }}
                </AccordionItem>
              ))}
            </Accordion>
          ) : (
            <Alert status="info">
              <AlertIcon />
              <Text>
                No migration runs found for <Code>{procedureName}</Code>.
              </Text>
            </Alert>
          )}
        </DrawerBody>
      </DrawerContent>
    </Drawer>
  );
}
